import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw, Trash2, MapPin } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkMoveDialog } from "@/components/BulkPlace";

const TABS = [
  ["wines", "Deleted wines"],
  ["deleted", "Deleted bottles"],
  ["consumed", "Consumed"],
  ["sold", "Sold"],
  ["lost", "Lost"],
];

const useList = (tab) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    setRows(null);
    return (tab === "wines" ? api.get("/trash/wines") : api.get("/bottles/archive", { params: { status: tab } })).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [tab]);
  useEffect(() => {
    load();
  }, [load]);
  return [rows, load];
};

const Actions = ({ tab, sel, rows, isOwner, onDone, onPlace }) => {
  const ids = sel;
  const call = async (path, msg, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      await api.post(path, { ids });
      toast.success(msg);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!ids.length) return <p className="text-xs text-[#6E665D] h-9 flex items-center">Select one or more to change them.</p>;
  const base = tab === "wines" ? "/wines" : "/bottles";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="archive-actions">
      <span className="text-sm text-[#D4AF37]" data-testid="archive-selected-count">{ids.length} selected</span>
      {tab === "wines" || tab === "deleted" ? (
        isOwner && (
          <>
            <Button size="sm" onClick={() => call(`${base}/restore`, "Restored")} className="bg-[#D4AF37] hover:bg-[#E5A93C] text-[#0D0A0B]" data-testid="archive-restore-button"><RotateCcw className="w-3.5 h-3.5 mr-2" />Restore</Button>
            <Button size="sm" variant="outline" onClick={() => call(`${base}/purge`, "Deleted for good", "Delete for good? This cannot be undone.")} className="text-[#E57373] border-[#8C2B36]/50" data-testid="archive-purge-button"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete for good</Button>
          </>
        )
      ) : (
        <Button size="sm" onClick={() => onPlace(rows.filter((r) => ids.includes(r.id)).map((r) => r.barcode))} className="bg-[#D4AF37] hover:bg-[#E5A93C] text-[#0D0A0B]" data-testid="archive-back-to-cellar-button"><MapPin className="w-3.5 h-3.5 mr-2" />Back in the cellar…</Button>
      )}
    </div>
  );
};

const Row = ({ r, tab, on, toggle, open }) => (
  <tr className="border-t border-[#2E252A] hover:bg-[#1E181B]" data-testid={`archive-row-${r.id}`}>
    <td className="pl-4 py-3 w-8"><Checkbox checked={on} onCheckedChange={() => toggle(r.id)} data-testid={`archive-select-${r.id}`} /></td>
    {tab !== "wines" && <td className="px-4 py-3 font-mono text-[#F5F0EB]">{r.barcode}</td>}
    <td className="px-4 py-3">
      <button onClick={() => tab !== "wines" && open(r.wine_id)} className="text-left">
        <span className="font-mono text-xs text-[#D4AF37] mr-2">{r.vintage || "NV"}</span>
        <span className="font-serif text-[#F5F0EB]">{tab === "wines" ? r.name : r.wine_name}</span>
        <span className="block text-xs text-[#A89F91]">{r.producer}</span>
      </button>
    </td>
    <td className="px-4 py-3 text-xs text-[#A89F91]">{tab === "wines" ? `${r.bottle_count} bottle${r.bottle_count === 1 ? "" : "s"}` : [r.location, r.box_number && `box ${r.box_number}`, r.sale_name, r.note].filter(Boolean).join(" · ") || "—"}</td>
    <td className="px-4 py-3 text-xs text-[#6E665D] whitespace-nowrap">{fmtDateTime(tab === "wines" ? r.deleted_at : r.changed_at)}{r.changed_by ? ` · ${r.changed_by}` : ""}</td>
  </tr>
);

export default function Archive() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("wines");
  const [sel, setSel] = useState([]);
  const [placing, setPlacing] = useState([]);
  const [rows, load] = useList(tab);
  const refresh = () => {
    setSel([]);
    load();
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Removed</p>
      <h2 className="font-serif text-4xl sm:text-5xl text-[#F5F0EB] mb-3">Deleted, consumed, sold & lost</h2>
      <p className="text-sm text-[#A89F91] mb-8 max-w-2xl">Change your mind here: put consumed, sold or lost bottles back in a location, or restore what was deleted by mistake.</p>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="archive-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); setSel([]); }} data-testid={`archive-tab-${k}`}
            className={`rounded-full border px-4 py-2 text-sm transition-colors ${tab === k ? "border-[#D4AF37] bg-[#D4AF37] text-[#0D0A0B]" : "border-[#2E252A] text-[#A89F91] hover:border-[#D4AF37]"}`}>{label}</button>
        ))}
      </div>
      <div className="mb-3"><Actions tab={tab} sel={sel} rows={rows || []} isOwner={user.role === "owner"} onDone={refresh} onPlace={setPlacing} /></div>
      <div className="rounded-xl border border-[#2E252A] overflow-x-auto" data-testid="archive-table">
        <table className="w-full text-sm">
          <tbody>
            {(rows || []).map((r) => <Row key={r.id} r={r} tab={tab} on={sel.includes(r.id)} toggle={toggle} open={(id) => navigate(`/wines/${id}`)} />)}
            {rows && !rows.length && <tr><td className="px-4 py-12 text-center text-[#6E665D]" data-testid="archive-empty">Nothing here.</td></tr>}
            {!rows && <tr><td className="px-4 py-12 text-center font-mono text-[#6E665D]">Loading…</td></tr>}
          </tbody>
        </table>
      </div>
      <BulkMoveDialog barcodes={placing} onClose={() => setPlacing([])} onDone={refresh} />
    </div>
  );
}
